import { useState } from 'react';
import { Check, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/design/components';
import { WEEKDAYS, toDateKey } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';
import { cn } from '@/lib/cn';
import {
  RECURRENCE_LABEL,
  type Recurrence,
  type TemplateItemValue,
  type TemplateValues,
} from './templateValues';

/**
 * תבנית צ'קליסט: שם, סניפים וחזרתיות.
 *
 * בחלון ולא בשורה: תבנית היא ארבע החלטות, והשורה הצרה שהיתה כאן
 * הכילה שתיים מהן. מה שלא היה על המסך גם לא נשאל.
 *
 * בלי סניף = כל הסניפים, וזו גם ההתנהגות שהיתה. בחירה מרובה
 * קיימת כי "ספירת מלאי בשלושה סניפים" חייבה קודם שלוש תבניות
 * זהות, ושינוי נוסח היה צריך לקרות שלוש פעמים.
 */
export function TemplateDialog({
  initial,
  branches,
  isSaving,
  onSave,
  onClose,
}: {
  initial: TemplateValues;
  branches: { id: string; name: string; color?: string | null }[];
  isSaving: boolean;
  onSave: (values: TemplateValues) => Promise<unknown>;
  onClose: () => void;
}) {
  const [values, setValues] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof TemplateValues>(key: K, value: TemplateValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  /**
   * מחושב מהמצב הקודם ולא מ values שנתפס ברינדור.
   * שתי לחיצות באותו tick היו קוראות את אותו ערך ישן, והשנייה
   * היתה מוחקת את הראשונה.
   */
  const toggle = <K extends 'branchIds' | 'weekdays'>(
    key: K,
    value: TemplateValues[K][number],
  ) =>
    setValues((current) => {
      const list = current[key] as (typeof value)[];
      return {
        ...current,
        [key]: list.includes(value)
          ? list.filter((one) => one !== value)
          : [...list, value],
      };
    });

  const editItem = (index: number, patch: Partial<TemplateItemValue>) =>
    setValues((current) => ({
      ...current,
      items: current.items.map((item, at) => (at === index ? { ...item, ...patch } : item)),
    }));

  const canSave =
    values.name.trim() !== '' &&
    (values.recurrence !== 'weekly' || values.weekdays.length > 0) &&
    (values.recurrence !== 'monthly' || values.dayOfMonth !== null) &&
    (values.recurrence !== 'once' || values.onDate !== null);

  return (
    <div className="fixed inset-0 z-40 grid items-end bg-charcoal/50 p-0 sm:place-items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={initial.id ? 'עריכת תבנית' : 'תבנית חדשה'}
        className="grid max-h-[90dvh] w-full gap-4 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-lg sm:rounded-lg"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="m-0 font-display text-display-m font-semibold">
            {initial.id ? 'עריכת תבנית' : 'תבנית חדשה'}
          </h2>
          <button
            type="button"
            aria-label="סגירה"
            onClick={onClose}
            className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-ink hover:bg-cream"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            void onSave({
              ...values,
              items: values.items.filter((item) => item.text.trim() !== ''),
            })
              .then(onClose)
              .catch((caught: unknown) => setError(errorMessage(caught)));
          }}
        >
          <label className="grid gap-1">
            <span className="text-label font-medium">שם התבנית</span>
            <input
              autoFocus
              value={values.name}
              onChange={(event) => set('name', event.target.value)}
              placeholder="למשל: פתיחת סניף"
              className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink placeholder:text-muted"
            />
          </label>

          <fieldset className="m-0 grid gap-2 border-0 p-0">
            <legend className="p-0 text-label font-medium">לאילו סניפים</legend>
            <div className="flex flex-wrap gap-2">
              <Chip
                active={values.branchIds.length === 0}
                onClick={() => set('branchIds', [])}
              >
                כל הסניפים
              </Chip>
              {branches.map((branch) => (
                <Chip
                  key={branch.id}
                  active={values.branchIds.includes(branch.id)}
                  color={branch.color}
                  onClick={() => toggle('branchIds', branch.id)}
                >
                  {branch.name}
                </Chip>
              ))}
            </div>
            <p className="m-0 text-body-sm text-muted">
              בלי בחירה התבנית חלה על כל הסניפים, גם על כאלה שייפתחו בעתיד.
            </p>
          </fieldset>

          <fieldset className="m-0 grid gap-2 border-0 p-0">
            <legend className="p-0 text-label font-medium">מתי זה חוזר</legend>
            <div className="grid gap-1">
              {(Object.keys(RECURRENCE_LABEL) as Recurrence[]).map((option) => (
                <label key={option} className="flex min-h-11 items-center gap-2 text-body">
                  <input
                    type="radio"
                    name="recurrence"
                    checked={values.recurrence === option}
                    onChange={() => set('recurrence', option)}
                    className="size-5"
                  />
                  {RECURRENCE_LABEL[option]}
                </label>
              ))}
            </div>

            {values.recurrence === 'weekly' && (
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((name, index) => (
                  <Chip
                    key={name}
                    active={values.weekdays.includes(index)}
                    onClick={() => toggle('weekdays', index)}
                  >
                    {name}
                  </Chip>
                ))}
              </div>
            )}

            {values.recurrence === 'monthly' && (
              <label className="grid max-w-40 gap-1">
                <span className="text-label text-muted">יום בחודש</span>
                <input
                  type="number"
                  min={1}
                  max={31}
                  dir="ltr"
                  value={values.dayOfMonth ?? ''}
                  onChange={(event) =>
                    set('dayOfMonth', event.target.value ? Number(event.target.value) : null)
                  }
                  className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
                />
                <span className="text-body-sm text-muted">
                  31 יחול גם בחודש קצר, ביום האחרון שלו.
                </span>
              </label>
            )}

            {values.recurrence === 'once' && (
              <label className="grid max-w-56 gap-1">
                <span className="text-label text-muted">תאריך</span>
                <input
                  type="date"
                  dir="ltr"
                  min={toDateKey(new Date())}
                  value={values.onDate ?? ''}
                  onChange={(event) => set('onDate', event.target.value || null)}
                  className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
                />
              </label>
            )}
          </fieldset>

          {/*
            המשימות נכתבות כאן ולא רק אחרי השמירה: מי שיוצר
            צ'קליסט כבר יודע מה יש בו, ושתי פעולות נפרדות הן שתי
            הזדמנויות לשכוח את השנייה.
          */}
          <fieldset className="m-0 grid gap-2 border-0 p-0">
            <legend className="p-0 text-label font-medium">
              המשימות{' '}
              {values.items.length > 0 && (
                <span className="text-muted">({values.items.length})</span>
              )}
            </legend>

            {values.items.length > 0 && (
              <ul className="m-0 grid list-none gap-1 p-0">
                {values.items.map((item, index) => (
                  <li key={item.id ?? `new-${index}`} className="flex items-center gap-2">
                    <input
                      value={item.text}
                      aria-label={`משימה ${index + 1}`}
                      onChange={(event) => editItem(index, { text: event.target.value })}
                      className="min-h-11 min-w-0 grow rounded-sm border border-muted bg-surface px-2 text-body-sm text-ink"
                    />
                    <label className="flex shrink-0 items-center gap-1 text-label text-muted">
                      <input
                        type="checkbox"
                        checked={item.requires_photo}
                        onChange={(event) =>
                          editItem(index, { requires_photo: event.target.checked })
                        }
                        className="size-5"
                      />
                      צילום
                    </label>
                    <button
                      type="button"
                      aria-label={`מחיקת ${item.text || 'המשימה'}`}
                      onClick={() =>
                        setValues((current) => ({
                          ...current,
                          items: current.items.filter((_, at) => at !== index),
                        }))
                      }
                      className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-muted hover:bg-cream hover:text-ink"
                    >
                      <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <Button
              variant="secondary"
              onClick={() =>
                setValues((current) => ({
                  ...current,
                  items: [...current.items, { text: '', requires_photo: false }],
                }))
              }
            >
              <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
              הוספת משימה
            </Button>
            <p className="m-0 text-body-sm text-muted">
              אפשר גם להוסיף ולסדר אותן אחר כך, מתוך התבנית ברשימה.
            </p>
          </fieldset>

          <label className="grid max-w-40 gap-1">
            <span className="text-label font-medium">עד איזו שעה</span>
            <input
              type="time"
              dir="ltr"
              value={values.dueTime ? values.dueTime.slice(0, 5) : ''}
              onChange={(event) => set('dueTime', event.target.value || null)}
              className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
            />
            <span className="text-body-sm text-muted">
              ריק = בלי מועד. אחרי השעה הזו צ׳קליסט שלא התחיל מסומן באדום.
            </span>
          </label>

          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" disabled={isSaving || !canSave}>
              {initial.id ? 'שמירת השינויים' : 'יצירת התבנית'}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              ביטול
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * צ'יפ בחירה.
 *
 * נבחר = נצבע, כמו בלוח הסידור. מסגרת כהה יותר אינה נראית, וזו
 * בדיוק הטעות שתוקנה שם. לסניף יש צבע משלו ולכן הוא משמש כאן;
 * ליום בשבוע אין, והוא מקבל את צבע הזית של המערכת.
 *
 * הצבע אינו לבד: יש סימן וי, והשם תמיד על הצ'יפ (כלל 10).
 */
function Chip({
  active,
  color,
  onClick,
  children,
}: {
  active: boolean;
  color?: string | null;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-pill border px-3 text-body-sm transition-colors',
        active
          ? 'border-ink font-semibold text-ink'
          : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
        active && !color && 'bg-olive',
      )}
      style={active && color ? { backgroundColor: color } : undefined}
    >
      {active && <Check size={16} strokeWidth={2.5} aria-hidden="true" className="shrink-0" />}
      {children}
    </button>
  );
}
